import { Zap, ShieldCheck, GitMerge } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

export default function Footer() {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';
  const currentYear = new Date().getFullYear();

  const links = {
    product: [
      { label: isTurkish ? 'Özellikler' : 'Features', href: '/#studio' },
      { label: isTurkish ? 'Fiyatlandırma' : 'Pricing', href: '/pricing' },
      { label: 'Marketplace', href: '/marketplace' },
      { label: isTurkish ? 'Lisanslama' : 'Licensing', href: '/licensing' },
      { label: isTurkish ? 'Entegrasyonlar' : 'Integrations', href: '/integrations' },
      { label: isTurkish ? 'İş Akışı Senaryoları' : 'Workflow Scenarios', href: '/case-studies' },
    ],
    company: [
      { label: isTurkish ? 'Hakkımızda' : 'About', href: '/about' },
      { label: 'Blog', href: '/blog' },
      { label: isTurkish ? 'İletişim' : 'Contact', href: '/contact' },
      { label: 'FAQ', href: '/faq' },
    ],
    legal: [
      { label: isTurkish ? 'Gizlilik' : 'Privacy', href: '/privacy-policy' },
      { label: isTurkish ? 'Şartlar' : 'Terms', href: '/terms-of-service' },
      { label: isTurkish ? 'Güven ve Güvenlik' : 'Trust & Security', href: '/security' },
      { label: isTurkish ? 'Açık Bildirimi' : 'Disclosure', href: '/security-policy' },
      { label: isTurkish ? 'Erişilebilirlik' : 'Accessibility', href: '/accessibility' },
    ],
  };

  return (
    <footer className="bg-[#060710] border-t border-white/10 py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-16">
          
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <a href="/" className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-white flex items-center justify-center">
                <Zap className="w-4 h-4 text-black" />
              </div>
              <span className="font-bold text-lg text-white tracking-tight">NEXUS</span>
            </a>
            <p className="text-sm text-white/65 max-w-xs leading-relaxed">
              {isTurkish 
                ? 'AI destekli endüstriyel tasarım platformu.'
                : 'AI-powered industrial design platform.'}
            </p>
            <div className="mt-5 space-y-1.5">
              <a href="mailto:alihan@nexusdesignai.com" className="block text-xs text-white/70 hover:text-white transition-colors">
                alihan@nexusdesignai.com
              </a>
              <p className="text-xs text-white/70">North Ridgeville, Ohio — USA</p>
              <p className="text-[10px] text-white/65 leading-relaxed">
                {isTurkish
                  ? 'Kurucu liderliğinde Ohio merkezli ürün projesi · sözleşme yapan hukuki kişi imzalı belgede açıklanır.'
                  : 'Founder-led Ohio product project · contracting legal entity disclosed in signed documents.'}
              </p>
              <p className="text-xs text-white/70">
                <span className="text-white/65">Alihan Demirhan</span>
                <span aria-hidden="true" className="mx-1.5">·</span>
                <span className="font-mono uppercase tracking-wider">
                  {isTurkish ? 'Kurucu' : 'Founder'}
                </span>
              </p>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-white/70 mb-4">
              {isTurkish ? 'Ürün' : 'Product'}
            </h4>
            <ul className="space-y-3">
              {links.product.map((link, i) => (
                <li key={i}>
                  <a href={link.href} className="text-sm text-white/70 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-white/70 mb-4">
              {isTurkish ? 'Şirket' : 'Company'}
            </h4>
            <ul className="space-y-3">
              {links.company.map((link, i) => (
                <li key={i}>
                  <a href={link.href} className="text-sm text-white/70 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-white/70 mb-4">
              {isTurkish ? 'Yasal' : 'Legal'}
            </h4>
            <ul className="space-y-3">
              {links.legal.map((link, i) => (
                <li key={i}>
                  <a href={link.href} className="text-sm text-white/70 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Engineering Trust Strip — automated quality signals for investors */}
        <div className="mb-8 pt-8 border-t border-white/10">
          {(() => {
            const repo = process.env.REACT_APP_GITHUB_REPO_URL;
            return (
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                {repo && (
                <a
                  href={`${repo}/actions/workflows/backend-ci.yml`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="trust-badge-tests"
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-emerald-400/20 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.08] hover:border-emerald-400/40 transition-colors"
                  title={isTurkish ? 'GitHub Actions çalışmalarını gör' : 'View GitHub Actions runs'}
                >
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-200">
                    {isTurkish ? 'otomatik test paketi' : 'automated test suite'}
                  </span>
                  <span className="text-[10px] font-mono text-white/70">·</span>
                  <span className="text-[10px] font-mono text-white/65">
                    {isTurkish ? 'her commit\'te' : 'every commit'}
                  </span>
                </a>
                )}

                {repo && (
                <a
                  href={`${repo}/actions`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="trust-badge-cicd"
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-cyan-400/20 bg-cyan-500/[0.04] hover:bg-cyan-500/[0.08] hover:border-cyan-400/40 transition-colors"
                  title={isTurkish ? 'CI/CD pipeline\'ını gör' : 'View CI/CD pipeline'}
                >
                  <GitMerge className="w-3.5 h-3.5 text-cyan-300" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-200">
                    {isTurkish ? 'CI/CD aktif' : 'CI/CD enabled'}
                  </span>
                  <span className="text-[10px] font-mono text-white/70">·</span>
                  <span className="text-[10px] font-mono text-white/65">
                    GitHub Actions
                  </span>
                </a>
                )}

                <div
                  data-testid="trust-badge-stack"
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
                >
                  <span className="w-1.5 h-1.5 bg-white/60" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-white/70">
                    FastAPI · React · MongoDB
                  </span>
                </div>
              </div>
            );
          })()}
          <p className="mt-3 text-center text-[10px] font-mono text-white/70 leading-relaxed max-w-2xl mx-auto">
            {isTurkish
              ? 'Doğrulama-öncelikli mühendislik. Otomatik izole test paketleri CI\'da çalışır — üretim verisine sıfır temas; çıktılar ön niteliktedir ve nitelikli inceleme gerektirir.'
              : 'Validation-first engineering. Automated isolated test suites run in CI — zero production data touches; outputs are preliminary and require qualified review.'}
          </p>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/70">
            © {currentYear} Nexus Design AI. {isTurkish ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}
          </p>
          <div className="flex items-center gap-6">
            {process.env.REACT_APP_TWITTER_URL && (
            <a href={process.env.REACT_APP_TWITTER_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" aria-label="Twitter / X">
              <span className="text-xs">Twitter</span>
            </a>
            )}
            {process.env.REACT_APP_LINKEDIN_URL && (
            <a href={process.env.REACT_APP_LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" aria-label="LinkedIn">
              <span className="text-xs">LinkedIn</span>
            </a>
            )}
            {process.env.REACT_APP_GITHUB_REPO_URL && (
            <a href={process.env.REACT_APP_GITHUB_REPO_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors" aria-label="GitHub">
              <span className="text-xs">GitHub</span>
            </a>
            )}
            <a href="mailto:alihan@nexusdesignai.com" className="text-white/70 hover:text-white transition-colors" aria-label="Email">
              <span className="text-xs">Email</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
